/**
 * BetaDataImportService.ts
 * Service to import and integrate existing user data from beta.redheli.com
 * Handles returning users who have already completed their journey
 */

import { Story, ChatMessage } from './AIStoryService';

// Beta story structure from personal_stories table
export interface BetaStory {
  id: string;
  title: string;
  whenDidThisHappen?: string;
  whatWasTheProblem?: string;
  howDidItMakeYouFeel?: string;
  whatWasYourSolution?: string;
  wereYouTempted?: string;
  whyDidYouChooseToAct?: string;
  wereThereDoubtMoments?: string;
  howDidSolutionWork?: string;
  howDidThisMakeYouFeel?: string;
  whatDidYouLearnAboutYourself?: string;
  whatDidYouLearnAboutOthers?: string;
  didItChangeYourPerspective?: string;
  sensoryMemory?: string;
  songOrObjectAnchor?: string;
  wereYouProud?: string;
  created_at?: string;
  story_type?: 'personal_story' | 'work_story' | 'brand_story';
}

// PostFlight data structure from Step 6
export interface BetaPostFlightData {
  communityConnections?: Array<{
    id: string;
    name: string;
    email?: string;
    relationship?: string;
    userId?: string;
    tags?: string[];
  }>;
  generatedStories?: Array<{
    id: string;
    timestamp: string;
    script: Array<{ narration: string; imagePrompt: string }>;
    images: string[];
    musicUrl: string;
    lyrics: string;
    metadata?: any;
  }>;
  journeyCompleted?: boolean;
  completedAt?: string;
}

// Complete Beta export structure
export interface BetaExportData {
  exportMetadata?: {
    timestamp: string;
    version: string;
  };
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  journey?: {
    currentStep: number;
    completedSteps: number[];
    progress: any;
  };
  lifeStories?: {
    stories: BetaStory[];
    storyDetails?: any[];
  };
  helicopterConstruction?: {
    postFlightData?: BetaPostFlightData;
  };
  communityConnections?: {
    postFlightConnections?: any[];
    generatedStories?: any[];
  };
}

export class BetaDataImportService {
  /**
   * Check if user has existing Beta data
   */
  static async checkBetaUserData(userId: string): Promise<boolean> {
  try {
    // Check localStorage first for cached Beta data
    const cachedData = localStorage.getItem(`beta_data_${userId}`);
    if (cachedData) return true;

    // Check with Beta API (if available)
    // This would need to be implemented based on Beta's actual API
    // For now, return false
    return false;
  } catch (error) {
    console.error('Error checking Beta user data:', error);
    return false;
  }
  }

  /**
   * Import Beta export JSON file
   */
  static async importBetaExport(file: File): Promise<BetaExportData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const data = JSON.parse(content) as BetaExportData;
        
        // Validate it's a Beta export
        if (!data.exportMetadata || !data.lifeStories) {
          throw new Error('Invalid Beta export file format');
        }
        
        // Cache in localStorage for future sessions
        if (data.user?.id) {
          localStorage.setItem(`beta_data_${data.user.id}`, content);
        }
        
        resolve(data);
      } catch (error) {
        reject(new Error('Failed to parse Beta export file'));
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

  /**
   * Convert Beta story to chat conversation format
   * This recreates the conversational discovery process from the structured story
   */
  static convertBetaStoryToChat(story: BetaStory): ChatMessage[] {
    const messages: ChatMessage[] = [];
  
  // System welcome
  messages.push({
    role: 'system',
    text: 'Welcome back! I see you have an existing agency story. Let me help you explore it further.',
    timestamp: new Date()
  });

  // Map the 16 Beta questions to conversational format
  const questionMappings = [
    { field: 'title', prompt: 'Your story title', response: story.title },
    { field: 'whatWasTheProblem', prompt: 'The challenge you observed', response: story.whatWasTheProblem },
    { field: 'whenDidThisHappen', prompt: 'When this happened', response: story.whenDidThisHappen },
    { field: 'howDidItMakeYouFeel', prompt: 'How it made you feel', response: story.howDidItMakeYouFeel },
    { field: 'whatWasYourSolution', prompt: 'Your solution', response: story.whatWasYourSolution },
    { field: 'wereYouTempted', prompt: 'Temptation to stay silent', response: story.wereYouTempted },
    { field: 'whyDidYouChooseToAct', prompt: 'Why you chose to act', response: story.whyDidYouChooseToAct },
    { field: 'wereThereDoubtMoments', prompt: 'Moments of doubt', response: story.wereThereDoubtMoments },
    { field: 'whatDidYouLearnAboutYourself', prompt: 'What you learned about yourself', response: story.whatDidYouLearnAboutYourself },
    { field: 'didItChangeYourPerspective', prompt: 'How it changed your perspective', response: story.didItChangeYourPerspective }
  ];

  // Build conversation from story data
  questionMappings.forEach(({ prompt, response }) => {
    if (response) {
      // User shares their story element
      messages.push({
        role: 'user',
        text: response,
        timestamp: new Date()
      });
      
      // AI reflects on the response
      messages.push({
        role: 'ai',
        text: `What stands out about ${prompt.toLowerCase()} is the clear demonstration of your agency. This reveals your pattern of taking ownership and creating positive change.`,
        timestamp: new Date(),
        analysis: {
          detectedPrinciple: 'Imported Agency Story'
        }
      });
    }
  });

  return messages;
};

/**
 * Convert Beta PostFlight generated story to Hover Story format
 */
  static convertBetaGeneratedStory(betaStory: any): Story {
    return {
    title: betaStory.title || 'Imported Agency Story',
    script: betaStory.script || [],
    images: betaStory.images || [],
    musicUrl: betaStory.musicUrl || '',
    lyrics: betaStory.lyrics || '',
    metadata: {
      ...betaStory.metadata,
      importedFromBeta: true,
      importDate: new Date().toISOString()
    }
  };
};

/**
 * Load user's Beta journey data
 */
  static loadBetaJourneyData(userId: string): BetaExportData | null {
    try {
    const cached = localStorage.getItem(`beta_data_${userId}`);
    if (cached) {
      return JSON.parse(cached);
    }
    return null;
  } catch (error) {
    console.error('Error loading Beta data:', error);
    return null;
  }
};

/**
 * Merge Beta data with Hover LIFE module
 */
  static mergeBetaDataWithHover(
  betaData: BetaExportData,
  currentChat: ChatMessage[]
): {
  mergedChat: ChatMessage[];
  existingStories: Story[];
  hasCompletedJourney: boolean;
} {
  const mergedChat = [...currentChat];
  const existingStories: Story[] = [];
  
  // Convert Beta stories to chat format
  if (betaData.lifeStories?.stories) {
    betaData.lifeStories.stories.forEach((story, index) => {
      if (index === 0) {
        // Use the first story as the primary chat conversation
        const storyChat = BetaDataImportService.convertBetaStoryToChat(story);
        mergedChat.push(...storyChat);
      }
    });
  }
  
  // Convert generated stories from PostFlight
  if (betaData.helicopterConstruction?.postFlightData?.generatedStories) {
    betaData.helicopterConstruction.postFlightData.generatedStories.forEach(genStory => {
      existingStories.push(BetaDataImportService.convertBetaGeneratedStory(genStory));
    });
  }
  
  // Check if journey was completed
  const hasCompletedJourney = betaData.helicopterConstruction?.postFlightData?.journeyCompleted || false;
  
  return {
    mergedChat,
    existingStories,
    hasCompletedJourney
  };
};

/**
 * Clear cached Beta data
 */
  static clearBetaData(userId: string): void {
    localStorage.removeItem(`beta_data_${userId}`);
};

/**
 * Export combined Hover + Beta data
 */
  static exportCombinedData(
  hoverStory: Story | null,
  betaData: BetaExportData | null,
  chatHistory: ChatMessage[]
): Blob {
  const exportData = {
    version: '2.0',
    platform: 'Hover + Beta Integration',
    exportDate: new Date().toISOString(),
    hover: {
      story: hoverStory,
      chatHistory: chatHistory.map(m => ({
        role: m.role,
        text: m.text,
        timestamp: m.timestamp
      }))
    },
    beta: betaData,
    metadata: {
      hasCompletedBetaJourney: betaData?.helicopterConstruction?.postFlightData?.journeyCompleted || false,
      storiesCount: (betaData?.lifeStories?.stories?.length || 0) + (hoverStory ? 1 : 0)
    }
  };
  
  const json = JSON.stringify(exportData, null, 2);
  return new Blob([json], { type: 'application/json' });
};
}